import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ApiRepos } from '../../../contracts/ContractVars';
import { useAuth } from '../../../auth/AuthProvider';
import { toast } from 'react-toastify';
import OrderComments from '../order/OrderComments';

const ViewOrder = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin, isManager, user } = useAuth();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [licensePlate, setLicensePlate] = useState('');
    const [hirePrice, setHirePrice] = useState('');
    const [isDateValid, setIsDateValid] = useState(true);
    const [commentsRefreshTrigger, setCommentsRefreshTrigger] = useState(0);

    const fetchOrder = useCallback(async () => {
        try {
            const response = await fetch(`${ApiRepos}order/${id}`, {
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to fetch order details');
            }

            const data = await response.json();
            setOrder(data);
            setLicensePlate(data.licensePlate || '');
            setHirePrice(data.quoteAmount ?? '');
            // Check if hire date is in the past
            const fromDateTime = new Date(data.hireFromDate);
            fromDateTime.setHours(data.hireFromHour);
            fromDateTime.setMinutes(data.hireFromMinute);
            setIsDateValid(fromDateTime > new Date());

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchOrder();
    }, [fetchOrder]);

    // Helper function to add system comments
    const addSystemComment = async (content) => {
        try {
            await fetch(`${ApiRepos}ordercomments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    orderId: id,
                    content: `[SYSTEM] ${content}`
                })
            });
        } catch (error) {
            console.error("Failed to add system comment:", error);
        }
    };

    const handleProcessOrder = async () => {
        try {
            const originalPlate = order.licensePlate;
            const originalStatus = order.status;
            const response = await fetch(`${ApiRepos}order/${id}/process`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(licensePlate)
            });

            if (!response.ok) throw new Error('Failed to process order');

            await fetchOrder();

            // Add system comment for license plate change
            if (originalPlate !== licensePlate) {
                if (originalPlate) {
                    await addSystemComment(`License plate updated from ${originalPlate} to ${licensePlate} by ${user.firstName} ${user.lastName}`);
                } else {
                    await addSystemComment(`License plate set to ${licensePlate} by ${user.firstName} ${user.lastName}`);
                }
            }

            // Add system comment for status change if needed
            if (originalStatus === 1) {
                await addSystemComment(`Order status changed from Received to Processed by ${user.firstName} ${user.lastName}`);
            }

            // Trigger comments refresh
            setCommentsRefreshTrigger(prev => prev + 1);

            toast.success(originalStatus === 1 ? 'Order processed successfully' : 'License plate updated successfully');
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleSetHirePrice = async () => {
        try {
            const parsedPrice = parseFloat(hirePrice);

            if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
                toast.error('Please enter a valid hire price');
                return;
            }

            const previousAmount = order.quoteAmount;
            const response = await fetch(`${ApiRepos}order/${id}/price`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(parsedPrice)
            });

            if (!response.ok) {
                throw new Error('Failed to set hire price');
            }

            const updatedOrder = await response.json();
            setOrder(updatedOrder);
            setHirePrice(updatedOrder.quoteAmount ?? parsedPrice);

            if (previousAmount !== updatedOrder.quoteAmount) {
                if (previousAmount || previousAmount === 0) {
                    await addSystemComment(`Hire price updated from £${previousAmount.toFixed(2)} +VAT to £${updatedOrder.quoteAmount.toFixed(2)} +VAT by ${user.firstName} ${user.lastName}`);
                } else {
                    await addSystemComment(`Hire price set to £${updatedOrder.quoteAmount.toFixed(2)} +VAT by ${user.firstName} ${user.lastName}`);
                }
            }

            setCommentsRefreshTrigger(prev => prev + 1);
            toast.success('Hire price saved');
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleCancelOrder = async () => {
        toast.warn(
            <div>
                <p className="mb-4">Are you sure you want to cancel this order?</p>
                <div className="flex justify-end gap-2">
                    <button
                        className="action-button cancel-button"
                        onClick={() => toast.dismiss('cancelOrderConfirm')}
                    >
                        No
                    </button>
                    <button
                        className="action-button edit-button"
                        onClick={async () => {
                            toast.dismiss('cancelOrderConfirm');
                            try {
                                const originalStatus = order.status;
                                const response = await fetch(`${ApiRepos}order/${id}/status`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    credentials: 'include',
                                    body: JSON.stringify(9)
                                });

                                if (!response.ok) throw new Error('Failed to cancel order');

                                // Add system comment for status change
                                await addSystemComment(`Order status changed from ${getStatusText(originalStatus)} to Cancelled by ${user.firstName} ${user.lastName}`);

                                await fetchOrder();
                                toast.success(`Order ${order.id} cancelled successfully`);
                            } catch (err) {
                                toast.error(`Error: ${err.message}`);
                            }
                        }}
                    >
                        Yes, Cancel
                    </button>
                </div>
            </div>,
            {
                position: "top-center",
                autoClose: false,
                closeButton: false,
                closeOnClick: false,
                draggable: false,
                toastId: 'cancelOrderConfirm'
            }
        );
    };

    const handleRequestChange = () => {
        const toastId = 'changeRequest';
        let changeNotes = '';

        toast.info(
            <div>
                <textarea
                    className="w-full p-2 mb-2 border rounded"
                    placeholder="Enter change request details..."
                    rows="3"
                    onChange={(e) => changeNotes = e.target.value}
                />
                <button
                    className="action-button edit-button"
                    onClick={async () => {
                        if (!changeNotes.trim()) {
                            toast.error('Please enter change details');
                            return;
                        }

                        toast.dismiss(toastId);
                        try {
                            const response = await fetch(`${ApiRepos}order/${id}/request-change`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                credentials: 'include',
                                body: JSON.stringify({ orderId: id, changeNotes })
                            });

                            if (!response.ok) throw new Error('Failed to submit change request');

                            // Add system comment for change request
                            await addSystemComment(`Change request submitted by ${user.firstName} ${user.lastName}: ${changeNotes}`);

                            toast.success('Change request submitted successfully');
                        } catch (err) {
                            toast.error(`Error: ${err.message}`);
                        }
                    }}
                >
                    Submit
                </button>
            </div>,
            {
                position: "top-center",
                autoClose: false,
                closeButton: true,
                closeOnClick: false,
                draggable: false,
                toastId
            }
        );
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;
    if (!order) return <div>Order not found</div>;

    const formatDateTime = (date, hour, minute) => {
        const formattedDate = new Date(date).toLocaleDateString();
        return `${formattedDate} ${hour}:${minute}`;
    };

    const getStatusText = (status) => {
        switch (status) {
            case 0: return 'Quote';
            case 1: return 'Received';
            case 5: return 'Processed';
            case 9: return 'Cancelled';
            case 10: return 'Cancelled Quote';
            default: return 'Unknown';
        }
    };

    // Determine what actions are available based on user role and order status
    const showEditButton =
        isAdmin || // Admins can always edit
        (order.status === 1 && ( // Status must be 1 (Received) for non-admins
            order.userId === user?.id || // User owns the order
            (isManager && order.businessId === user?.businessId) // Manager from same business
        ));

    // Show change request for non-admins when they can't edit
    const showChangeRequest = !isAdmin && !showEditButton;

    // Show admin controls
    const showAdminControls = isAdmin && order.status !== 9; // Not for cancelled orders

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h2>Order Ref {order.id} - Current Status: {getStatusText(order.status)}</h2>
                <div>
                    {showAdminControls && (
                        <div className="mb-4">
                            {order.status === 1 && !isDateValid ? (
                                <div className="text-red-600 bg-red-50 p-4 rounded-lg">
                                    <p className="font-medium mb-2">This order cannot be processed as the hire start date is in the past.</p>
                                    <p>Please either cancel the order or edit to update the dates.</p>
                                    <button
                                        onClick={handleCancelOrder}
                                        className="action-button cancel-button"
                                    >
                                        Cancel Order
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-4">
                                    {(order.status === 1 || order.status === 5) && (
                                        <>
                                            <div className="flex items-center gap-2">
                                                <label className="font-medium">Hire Price (&pound;):</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={hirePrice}
                                                    onChange={(e) => setHirePrice(e.target.value)}
                                                    placeholder="Enter hire price"
                                                    className="border rounded px-2 py-1 w-36"
                                                />
                                                <button
                                                    onClick={handleSetHirePrice}
                                                    className="action-button edit-button"
                                                >
                                                    Set Price
                                                </button>
                                            </div>
                                            <input
                                                type="text"
                                                value={licensePlate}
                                                onChange={(e) => setLicensePlate(e.target.value)}
                                                placeholder="Enter license plate"
                                                className="border rounded px-2 py-1"
                                            />
                                            <button
                                                onClick={handleProcessOrder}
                                                className="action-button edit-button"
                                            >
                                                {order.status === 1 ? 'Process Order' : 'Update License Plate'}
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {showEditButton && (
                        <>
                            <button
                                onClick={() => navigate(`/dashboard/order?edit=${order.id}`)}
                                className="action-button edit-button"
                            >
                                Edit Order
                            </button>
                            <button
                                onClick={handleCancelOrder}
                                className="action-button cancel-button"
                            >
                                Cancel Order
                            </button>
                        </>
                    )}
                    {showChangeRequest && (
                        <button
                            onClick={handleRequestChange}
                            className="action-button edit-button"
                        >
                            Request Change
                        </button>
                    )}
                </div>
            </div>

            {/* Order Comments Component */}
            <div className="mb-6">
                <OrderComments orderId={id} refreshTrigger={commentsRefreshTrigger} />
            </div>

            <div className="bg-white shadow-md rounded-lg p-6">
                <div className="grid grid-cols-2 gap-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-3">Driver Details</h3>
                        <p><span className="font-medium">Name:</span> {order.driverName}</p>
                        <p><span className="font-medium">Email:</span> {order.driverEmail}</p>
                        <p><span className="font-medium">Mobile:</span> {order.mobile}</p>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold mb-3">Hire Details</h3>
                        <p><span className="font-medium">Vehicle Type:</span> {order.vehicleTypeName || 'N/A'}</p>
                        <p><span className="font-medium">From:</span> {formatDateTime(order.hireFromDate, order.hireFromHour, order.hireFromMinute)}</p>
                        <p><span className="font-medium">To:</span> {formatDateTime(order.hireToDate, order.hireToHour, order.hireToMinute)}</p>
                        <p><span className="font-medium">Logistics:</span> {order.logistics}</p>
                        {order.quoteAmount !== null && order.quoteAmount !== undefined && (
                            <p><span className="font-medium">Price for Hire:</span> &pound;{order.quoteAmount.toFixed(2)} +VAT</p>
                        )}
                    </div>

                    {order.logistics === 'Delivery' && (
                        <div>
                            <h3 className="text-lg font-semibold mb-3">Delivery Address</h3>
                            <p>{order.addressLine1}</p>
                            {order.addressLine2 && <p>{order.addressLine2}</p>}
                            <p>{order.town}</p>
                            <p>{order.postCode}</p>
                        </div>
                    )}

                    {order.logistics === 'Delivery' && !order.collectionAddressSameAsDelivery && (
                        <div>
                            <h3 className="text-lg font-semibold mb-3">Collection Address</h3>
                            <p>{order.collectionAddressLine1}</p>
                            {order.collectionAddressLine2 && <p>{order.collectionAddressLine2}</p>}
                            <p>{order.collectionTown}</p>
                            <p>{order.collectionPostCode}</p>
                        </div>
                    )}

                    <div>
                        <h3 className="text-lg font-semibold mb-3">Additional Details</h3>
                        <p><span className="font-medium">Purpose:</span> {order.purpose || 'N/A'}</p>
                        <p><span className="font-medium">Notes:</span> {order.notes || 'N/A'}</p>
                        <p><span className="font-medium">PO Number:</span> {order.poNumber || 'N/A'}</p>
                        <p><span className="font-medium">Created By:</span> {order.requestedByUser}</p>
                        <p><span className="font-medium">Created Date:</span> {new Date(order.createdDate).toLocaleString()}</p>
                        {order.updatedByUser && (
                            <p><span className="font-medium">Last Updated By:</span> {order.updatedByUser}</p>
                        )}
                        {order.licensePlate && (
                            <p><span className="font-medium">License Plate:</span> {order.licensePlate}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewOrder;
